import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft, User, Users, AlertCircle } from 'lucide-react';
import { ScanCamera } from '@/components/ScanCamera';
import type { Session } from '@/types';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('qrhost_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function ScanPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'scan' | 'preview' | 'success'>('scan');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [preview, setPreview] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('qrhost_token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleScan = async (id: string) => {
    setSessionId(id);
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/scan/validate/${id}`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        throw new Error('QR no válido o sesión inactiva');
      }
      setPreview(data.session as Session);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al validar QR');
      setStep('scan');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          session_id: sessionId,
          device_info: navigator.userAgent,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al registrar escaneo');
      }
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen py-6 px-4" style={{ background: 'var(--qr-bg)' }}>
      <div className="w-full max-w-md flex flex-col gap-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-lg transition-transform active:scale-95"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold">Escanear QR</h1>
        </div>

        {step === 'scan' && (
          <>
            <ScanCamera onScan={handleScan} />
            {loading && (
              <p className="text-center text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
                Validando...
              </p>
            )}
            {error && (
              <div
                className="flex items-center gap-2 p-3 rounded-xl text-sm"
                style={{ background: 'var(--qr-danger)22', color: 'var(--qr-danger)', border: '1px solid var(--qr-danger)' }}
              >
                <AlertCircle size={16} />
                {error}
              </div>
            )}
          </>
        )}

        {step === 'preview' && preview && (
          <div
            className="flex flex-col gap-4 p-5 rounded-2xl animate-fade-in"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <h2 className="text-lg font-bold text-center">Cliente detectado</h2>
            <div className="flex flex-col gap-2 text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
              <div className="flex items-center gap-2">
                <User size={16} />
                <span className="font-semibold text-base" style={{ color: 'var(--qr-text)' }}>
                  {preview.client_name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users size={16} />
                <span>{preview.party_size} comensales</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide">Estado:</span>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded"
                  style={{
                    background:
                      preview.status === 'waiting'
                        ? 'var(--qr-primary)'
                        : preview.status === 'notified'
                        ? 'var(--qr-success)'
                        : 'var(--qr-border)',
                    color: '#fff',
                  }}
                >
                  {preview.status}
                </span>
              </div>
            </div>
            {error && (
              <p className="text-sm" style={{ color: 'var(--qr-danger)' }}>
                {error}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setStep('scan')}
                className="flex-1 py-3 rounded-xl font-semibold transition-transform active:scale-[0.98]"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              >
                Volver
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className="flex-1 py-3 rounded-xl font-semibold transition-transform active:scale-[0.98] disabled:opacity-60"
                style={{ background: 'var(--qr-primary)', color: '#fff' }}
              >
                {loading ? 'Registrando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div
            className="flex flex-col items-center gap-4 p-6 rounded-2xl animate-fade-in"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <CheckCircle size={48} style={{ color: 'var(--qr-success)' }} />
            <h2 className="text-xl font-bold">¡Escaneo registrado!</h2>
            <p className="text-sm text-center" style={{ color: 'var(--qr-text-secondary)' }}>
              El cliente ha sido añadido a la lista de espera.
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => {
                  setStep('scan');
                  setSessionId(null);
                  setPreview(null);
                  setError(null);
                }}
                className="flex-1 py-3 rounded-xl font-semibold transition-transform active:scale-[0.98]"
                style={{ background: 'var(--qr-primary)', color: '#fff' }}
              >
                Escanear otro
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 py-3 rounded-xl font-semibold transition-transform active:scale-[0.98]"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              >
                Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
